import type { FormSchemaVersion } from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { VisitTypeId } from '../../services/api/models/VisitTypeId';

/** US — unlikely in RogerDoc identifiers; avoids collision in composite ids */
export const SCHEMA_STUDIO_PICK_SEP = '\u001f';

export type FormSchemaStudioPick = {
  id: string;
  rogerDocFormType: RogerDocFormType;
  /** Canonical VisitTypeId for RogerDoc EMR APIs when multiple aliases share a form family */
  visitType: VisitTypeId;
  /** From GET /rogerdoc/form-type-catalog (`VISIT_TYPE_INFO.is_oasis`). */
  isOasis: boolean;
  /** `display_label` from GET /rogerdoc/form-type-catalog */
  formTypeDisplayLabel: string;
  /** Row label for schema dropdowns */
  label: string;
  schemaVersion: FormSchemaVersion;
};

/** One option per RogerDoc logical version: pairs `schema_version.{name,version}`. */
export function makeFormSchemaVersionPickId(
  schemaVersion: FormSchemaVersion
): string {
  return [schemaVersion.name, schemaVersion.version ?? ''].join(
    SCHEMA_STUDIO_PICK_SEP
  );
}

/** A pin resolved against the version list, and whether the match was exact. */
export type SchemaPinResolution = {
  pick: FormSchemaStudioPick | undefined;
  /**
   * False when the pin named a version that is no longer in the list and we
   * fell back to another revision with the same name. Callers should say so
   * rather than presenting the substitute as the pinned schema.
   */
  exact: boolean;
};

/**
 * Resolve a saved template's or draft's schema pin to a version pick.
 *
 * A pin is a (name, version) pair and several revisions can share a name, so
 * matching on the name alone can resolve to a different revision than the one
 * the layout was saved against -- and then every consumer (questions, print
 * preview) renders the wrong question set. Prefer the exact pair; fall back to
 * the name only when that revision is no longer in the list, and report the
 * fallback through `exact` so the editor can warn instead of silently showing
 * a plausible but wrong schema.
 *
 * A null/undefined `version` is a pin that never named a revision (drafts
 * carry one), so the name-only match is exact for it.
 */
export function resolveSchemaStudioPin(
  picks: FormSchemaStudioPick[],
  name: string | null | undefined,
  version?: string | null
): SchemaPinResolution {
  if (!name) return { pick: undefined, exact: true };
  if (version) {
    const exact = picks.find(
      p => p.schemaVersion.name === name && p.schemaVersion.version === version
    );
    if (exact) return { pick: exact, exact: true };
    const byName = picks.find(p => p.schemaVersion.name === name);
    return { pick: byName, exact: byName == null };
  }
  return { pick: picks.find(p => p.schemaVersion.name === name), exact: true };
}
